/**
 * PHARMAKON — pricing engine · purchase line and invoice totals (D-01…D-04, D-08, D-09, A-01).
 *
 * Owner of the formulas: design/ux/02_achats.md §2 (refonte-achats-stock) and research/REVENUE_MARGIN.md
 * §4.1; worked example ux/02 §2.6 = the golden fixture. Field names of the inputs are the Prisma
 * columns (`DetailAchat`, `FactureAchat`, DB-03 / DB-04); every computed amount is returned in
 * integer centimes (`*_centimes`), every percentage as a JS number at its storage scale.
 *
 * Pure: no I/O, no clock, no settings lookup — the pharmacy settings (A-06 tolerance) are passed in.
 * Timbre: the purchase flow applies none (ux/02 §2.2, DB-OP-17), so TTC = HT remisé + TVA.
 */
import {
  ECHELLE_MARGE_PCT,
  appliquerPct,
  diviserArrondi,
  diviserPlancher,
  multiplier,
  ratioPct,
  repartirPlusFortsRestes,
  somme,
  versCentiemesPct,
  versCentimes,
  versPct,
} from "./money.js";
import { ANOMALIES_PRICING, ETAT_ECART_TTC, MAITRE_REMISE, MAITRE_UG, TOTAUX_DECLARES } from "./constantes.js";

function estVide(v) {
  return v === null || v === undefined || v === "";
}

/** Non-negative safe integer (a quantity). null / "" → 0. */
function lireQuantite(valeur, champ) {
  if (estVide(valeur)) return 0;
  const n = typeof valeur === "number" ? valeur : Number(String(valeur).trim());
  if (!Number.isSafeInteger(n) || n < 0) {
    const e = new RangeError(`Quantité invalide (${champ}) : ${String(valeur)}`);
    e.code = "QUANTITE_INVALIDE";
    e.champ = champ;
    throw e;
  }
  return n;
}

/** A-01 — UG from UG % : floor(q × % ÷ 100). `pctCentiemes` in hundredths of a percent. */
export function ugDepuisPct(quantite, pctCentiemes) {
  if (pctCentiemes <= 0) return 0;
  return diviserPlancher(BigInt(quantite) * BigInt(pctCentiemes), 10000n);
}

/** A-01 — UG % back-computed from UG, rounded to 2 decimals (hundredths). null when q = 0. */
export function pctDepuisUg(ug, quantite) {
  if (quantite === 0) return null;
  return diviserArrondi(BigInt(ug) * 10000n, BigInt(quantite));
}

/**
 * One purchase line (ux/02 §2.1, D-02, D-04, D-05 A-13, D-08, A-01).
 *
 * @param {object} ligne
 * @param {number} ligne.quantite           Qté payée (billed units, UG excluded)
 * @param {number} [ligne.ug]               UG typed (master when `maitre_ug` = UG, or when given)
 * @param {number|string} [ligne.ug_pct]    UG % typed (master when `maitre_ug` = UG_PCT, or when UG is absent)
 * @param {"UG"|"UG_PCT"} [ligne.maitre_ug] the field typed last (A-01)
 * @param {*} ligne.prix_achat_ht           gross PU HT as typed (D-05 margin basis)
 * @param {*} [ligne.ristourne]             per-line discount in DA (D-01)
 * @param {*} [ligne.taux_tva]              0 / 9 / 19 (the validator owns the allowed list)
 * @param {*} [ligne.prix_vente_ppa]        PPA per unit
 * @param {*} [ligne.shp]                   SHP per unit
 * @param {number} [ligne.quantite_recue]   paid units received (D-08); defaults to `quantite`
 */
export function calculerLigne(ligne = {}) {
  const anomalies = [];
  const quantite = lireQuantite(ligne.quantite, "quantite");
  const quantiteRecue = estVide(ligne.quantite_recue) ? quantite : lireQuantite(ligne.quantite_recue, "quantite_recue");
  if (quantiteRecue > quantite) anomalies.push(ANOMALIES_PRICING.QUANTITE_RECUE_SUPERIEURE);

  // A-01 master rule: the field typed last wins; without the hint, a typed UG wins over a typed %.
  let maitre = ligne.maitre_ug;
  if (maitre !== MAITRE_UG.UG && maitre !== MAITRE_UG.UG_PCT) {
    maitre = !estVide(ligne.ug) ? MAITRE_UG.UG : !estVide(ligne.ug_pct) ? MAITRE_UG.UG_PCT : MAITRE_UG.UG;
  }
  let ug;
  let ugPctCentiemes;
  if (maitre === MAITRE_UG.UG_PCT) {
    ugPctCentiemes = versCentiemesPct(ligne.ug_pct);
    if (ugPctCentiemes < 0) anomalies.push(ANOMALIES_PRICING.MONTANT_NEGATIF);
    ug = ugDepuisPct(quantite, ugPctCentiemes);
  } else {
    ug = lireQuantite(ligne.ug, "ug");
    ugPctCentiemes = pctDepuisUg(ug, quantite);
  }

  const pu = versCentimes(ligne.prix_achat_ht);
  const ristourne = versCentimes(ligne.ristourne);
  const tauxTva = versCentiemesPct(ligne.taux_tva);
  const ppa = versCentimes(ligne.prix_vente_ppa);
  const shp = versCentimes(ligne.shp);
  if (pu < 0 || ristourne < 0 || tauxTva < 0 || ppa < 0 || shp < 0) {
    if (!anomalies.includes(ANOMALIES_PRICING.MONTANT_NEGATIF)) anomalies.push(ANOMALIES_PRICING.MONTANT_NEGATIF);
  }

  const brut = multiplier(quantite, pu); // D-02: UG never enter HT
  if (brut >= 0 && ristourne > brut) anomalies.push(ANOMALIES_PRICING.RISTOURNE_SUPERIEURE_AU_BRUT);
  const montantHt = brut - ristourne;
  const uniteMarge = ppa - pu; // D-05: gross PU HT, ristourne / remise globale never allocated into it

  return {
    quantite,
    ug,
    ug_pct: ugPctCentiemes === null ? null : versPct(ugPctCentiemes),
    maitre_ug: maitre,
    quantite_recue: quantiteRecue,
    manquant: Math.max(0, quantite - quantiteRecue), // D-08 / D-11 avoir produits suggestion
    entree_stock: quantiteRecue + ug, // D-08: UG always considered delivered
    taux_tva: versPct(tauxTva),
    taux_tva_centiemes: tauxTva,
    prix_achat_ht_centimes: pu,
    prix_vente_ppa_centimes: ppa,
    shp_centimes: shp,
    montant_brut_centimes: brut, // struck-through value, shown only when ristourne > 0 (B4)
    ristourne_centimes: ristourne,
    montant_ht_centimes: montantHt, // D-02 Line HT — the main value on the row
    montant_ppa_centimes: multiplier(ppa, quantite), // OP-ACH-03: paid units
    montant_shp_centimes: multiplier(shp, quantite),
    marge_pct: ratioPct(uniteMarge, pu, ECHELLE_MARGE_PCT), // A-13 (PPA − PU HT) ÷ PU HT
    marge_previsionnelle_centimes: somme([multiplier(uniteMarge, quantiteRecue), multiplier(ppa, ug)]), // ux/02 §2.4 (D-05)
    base_marge_centimes: multiplier(pu, quantiteRecue),
    anomalies,
  };
}

/** Groups the per-line TVA of one rate so Σ lines = TVA of the rate (P-7). */
function repartirTvaDuTaux(tvaTaux, bases) {
  const poids = bases.map((b) => Math.max(0, b));
  if (somme(poids) === 0) {
    // Only reachable in an anomaly state (remise > HT): no pro-rata basis, the rate total goes to
    // the first line of the rate so the per-line figures still sum to the header.
    return bases.map((_, k) => (k === 0 ? tvaTaux : 0));
  }
  return repartirPlusFortsRestes(tvaTaux, poids);
}

/**
 * Invoice totals (ux/02 §2.2, §2.3; D-01, D-03, D-09).
 *
 * @param {object[]} lignes   inputs of calculerLigne
 * @param {object} [entete]   `remise_globale_montant` (DA) or `remise_pct` (+ `maitre_remise`), and the
 *                            six declared totals (`montant_ht_declare`, `montant_ht_remise_declare`,
 *                            `montant_tva_declare`, `montant_ppa_declare`, `montant_shp_declare`,
 *                            `montant_facture_reelle` = TTC déclaré)
 * @param {object} parametres the pharmacy settings: `tolerance_ecart_ttc_da` (A-06) — required
 */
export function calculerTotaux(lignes, entete = {}, parametres) {
  if (!parametres || estVide(parametres.tolerance_ecart_ttc_da)) {
    throw new TypeError("calculerTotaux : parametres.tolerance_ecart_ttc_da est requis (A-06)");
  }
  const tolerance = versCentimes(parametres.tolerance_ecart_ttc_da);
  const calc = (lignes ?? []).map((l) => calculerLigne(l));
  const anomalies = [];
  for (const l of calc) for (const a of l.anomalies) if (!anomalies.includes(a)) anomalies.push(a);

  const montantHtLignes = somme(calc.map((l) => l.montant_ht_centimes));

  // D-03 remise globale: typed in DA, or in % of Total HT (R = round2(Total HT × p ÷ 100)).
  let maitreRemise = entete.maitre_remise;
  if (maitreRemise !== MAITRE_REMISE.MONTANT && maitreRemise !== MAITRE_REMISE.POURCENT) {
    maitreRemise = estVide(entete.remise_globale_montant) && !estVide(entete.remise_pct)
      ? MAITRE_REMISE.POURCENT
      : MAITRE_REMISE.MONTANT;
  }
  const remisePctCentiemes = versCentiemesPct(entete.remise_pct);
  const remise = maitreRemise === MAITRE_REMISE.POURCENT
    ? appliquerPct(montantHtLignes, remisePctCentiemes)
    : versCentimes(entete.remise_globale_montant);
  if (remise < 0 && !anomalies.includes(ANOMALIES_PRICING.MONTANT_NEGATIF)) anomalies.push(ANOMALIES_PRICING.MONTANT_NEGATIF);
  if (remise > montantHtLignes) anomalies.push(ANOMALIES_PRICING.REMISE_SUPERIEURE_AU_HT);
  const montantHt = montantHtLignes - remise; // HT remisé

  // D-03 allocation for TVA only — never displayed, never in the lot cost (D-05).
  const poidsHt = calc.map((l) => Math.max(0, l.montant_ht_centimes));
  let allocations;
  if (somme(poidsHt) === 0) allocations = calc.map((_, k) => (k === 0 ? remise : 0));
  else allocations = repartirPlusFortsRestes(remise, poidsHt);
  if (calc.length === 0) allocations = [];

  // TVA once per rate (paper invoices), then split back on the lines of the rate.
  const taux = [];
  calc.forEach((l, i) => {
    let g = taux.find((t) => t.taux_tva_centiemes === l.taux_tva_centiemes);
    if (!g) {
      g = { taux_tva_centiemes: l.taux_tva_centiemes, indices: [] };
      taux.push(g);
    }
    g.indices.push(i);
  });
  taux.sort((a, b) => a.taux_tva_centiemes - b.taux_tva_centiemes);
  const tvaLigne = calc.map(() => 0);
  const tvaParTaux = taux.map((g) => {
    const bases = g.indices.map((i) => calc[i].montant_ht_centimes - allocations[i]);
    const base = somme(bases);
    const montant = appliquerPct(base, g.taux_tva_centiemes);
    repartirTvaDuTaux(montant, bases).forEach((v, k) => { tvaLigne[g.indices[k]] = v; });
    return { taux_tva: versPct(g.taux_tva_centiemes), base_centimes: base, montant_centimes: montant };
  });
  const montantTva = somme(tvaParTaux.map((t) => t.montant_centimes));
  const montantTtc = montantHt + montantTva;

  const lignesCalc = calc.map((l, i) => ({
    ...l,
    remise_globale_allouee_centimes: allocations[i],
    base_tva_centimes: l.montant_ht_centimes - allocations[i],
    montant_tva_centimes: tvaLigne[i],
    // DB-04: net unit cost for the avoir valuation only (UX OP-03-03), never the margin basis.
    prix_achat_ht_net_centimes: l.quantite > 0 ? diviserArrondi(l.montant_ht_centimes - allocations[i], l.quantite) : null,
  }));

  const margeMontant = somme(calc.map((l) => l.marge_previsionnelle_centimes));
  const margeBase = somme(calc.map((l) => l.base_marge_centimes));

  const totaux = {
    lignes: lignesCalc,
    montant_brut_centimes: somme(calc.map((l) => l.montant_brut_centimes)),
    ristournes_centimes: somme(calc.map((l) => l.ristourne_centimes)),
    montant_ht_lignes_centimes: montantHtLignes,
    maitre_remise: maitreRemise,
    remise_pct: maitreRemise === MAITRE_REMISE.POURCENT ? versPct(remisePctCentiemes) : null,
    remise_globale_montant_centimes: remise,
    montant_ht_centimes: montantHt,
    tva_par_taux: tvaParTaux,
    montant_tva_centimes: montantTva,
    montant_ttc_centimes: montantTtc,
    montant_ppa_total_centimes: somme(calc.map((l) => l.montant_ppa_centimes)),
    montant_shp_total_centimes: somme(calc.map((l) => l.montant_shp_centimes)),
    marge_previsionnelle: {
      montant_centimes: margeMontant,
      base_centimes: margeBase,
      pct: ratioPct(margeMontant, margeBase, ECHELLE_MARGE_PCT),
    },
    quantite_entree_stock: somme(calc.map((l) => l.entree_stock)),
    quantite_manquante: somme(calc.map((l) => l.manquant)),
    tolerance_ecart_centimes: tolerance,
  };

  // D-09 — six écarts displayed (déclaré − calculé); only TTC drives the rule.
  const ecarts = {};
  for (const t of TOTAUX_DECLARES) {
    const declare = estVide(entete[t.declare]) ? null : versCentimes(entete[t.declare]);
    const calcule = totaux[t.calcule];
    const ecart = declare === null ? null : declare - calcule;
    ecarts[t.cle] = {
      declare_centimes: declare,
      calcule_centimes: calcule,
      ecart_centimes: ecart,
      hors_tolerance: ecart !== null && Math.abs(ecart) > tolerance,
    };
  }
  const ecartTtc = ecarts.ttc.ecart_centimes;
  let etat;
  if (ecartTtc === null) etat = ETAT_ECART_TTC.NON_SAISI;
  else if (ecartTtc > tolerance) etat = ETAT_ECART_TTC.AVOIR_FINANCIER_REQUIS;
  else if (ecartTtc < -tolerance) etat = ETAT_ECART_TTC.TOTAL_FOURNISSEUR_INFERIEUR;
  else etat = ETAT_ECART_TTC.CONFORME;

  totaux.ecarts = ecarts;
  totaux.regle_ttc = {
    etat,
    ecart_centimes: ecartTtc,
    avoir_financier_requis: etat === ETAT_ECART_TTC.AVOIR_FINANCIER_REQUIS,
    montant_avoir_financier_centimes: etat === ETAT_ECART_TTC.AVOIR_FINANCIER_REQUIS ? ecartTtc : 0,
  };
  totaux.anomalies = anomalies;
  // Validable = TTC déclaré entered (OP-ACH-15) and no domain anomaly; an écart never blocks (D-09).
  totaux.validable = etat !== ETAT_ECART_TTC.NON_SAISI && anomalies.length === 0;
  return totaux;
}
